import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Paper, CircularProgress } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { FusePageSimple } from '@fuse';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { API_BASE_URL } from '@config';
import componentRegistry from './componentRegistry';

const useStyles = makeStyles(theme => ({
	root: {
		backgroundColor: theme.palette.background.default
	},
	contentWrapper: {
		height: '100%'
	},
	fuseContent: {
		flex: '1 1 auto',
		minHeight: 0,
		display: 'flex',
		flexDirection: 'column'
	},
	content: {
		padding: theme.spacing(3),
		display: 'flex',
		flexDirection: 'column',
		flex: '1 1 auto',
		minHeight: 0
	},
	expirationBanner: {
		marginBottom: theme.spacing(3)
	},
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: 400
	},
	errorContainer: {
		padding: theme.spacing(3)
	}
}));

function LandingPage() {
	const classes = useStyles();
	const user = useSelector(({ auth }) => auth.user);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [campaignData, setCampaignData] = useState(null);

	useEffect(() => {
		if (user) {
			fetchCampaignData();
		} else {
			setLoading(false);
		}
	}, [user]);

	const fetchCampaignData = async () => {
		try {
			setLoading(true);
			const response = await axios.get(`${API_BASE_URL}/api/campaigns/me`);
			setCampaignData(response.data);
			setLoading(false);
		} catch (err) {
			console.error('Error fetching campaign data:', err);
			setError(err.response?.data?.message || 'Failed to load campaign information');
			setLoading(false);
		}
	};

	const renderComponent = () => {
		if (!campaignData || !campaignData.landingPageComponent) {
			return (
				<Paper className={classes.content}>
					<Typography variant="h4" gutterBottom>
						Welcome to Heidi!
					</Typography>
					<Typography variant="body1">
						Explore our platform and discover powerful computing capabilities.
					</Typography>
				</Paper>
			);
		}

		const ComponentToRender = componentRegistry[campaignData.landingPageComponent];

		if (!ComponentToRender) {
			console.warn(`Component ${campaignData.landingPageComponent} not found in registry`);
			return (
				<Paper className={classes.content}>
					<Typography variant="h4" gutterBottom>
						Welcome!
					</Typography>
					<Typography variant="body1">
						Welcome to your demo experience.
					</Typography>
				</Paper>
			);
		}

		return <ComponentToRender config={campaignData.landingPageConfig} branding={campaignData.customBranding} />;
	};

	if (loading) {
		return (
			<FusePageSimple
				classes={{
					root: classes.root,
					contentWrapper: classes.contentWrapper,
					content: classes.fuseContent
				}}
				content={
					<div className={classes.loadingContainer}>
						<CircularProgress />
					</div>
				}
			/>
		);
	}

	if (error) {
		return (
			<FusePageSimple
				classes={{
					root: classes.root,
					contentWrapper: classes.contentWrapper,
					content: classes.fuseContent
				}}
				content={
					<div className={classes.errorContainer}>
						<Alert severity="error">
							<AlertTitle>Error</AlertTitle>
							{error}
						</Alert>
					</div>
				}
			/>
		);
	}

	return (
		<FusePageSimple
			classes={{
				root: classes.root,
				contentWrapper: classes.contentWrapper,
				content: classes.fuseContent
			}}
			content={
				<div className={classes.content}>
					{renderComponent()}
				</div>
			}
		/>
	);
}

export default LandingPage;
